// @ts-strict-ignore
/* eslint-disable @typescript-eslint/no-useless-constructor */

/**
 * @module Conversations
 */
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { Call, CallRB, CallStatus } from 'rainbow-web-sdk/models/call.model';
import { Bubble, BubbleRB } from 'rainbow-web-sdk/models/bubble.model';
import { Subject, Subscription, filter } from 'rxjs';
import { AnswerMessage, Message, MessageID, MessageSide, MessageStatus, Urgency } from './message.model';
import type { Webinar } from 'rainbow-web-sdk/models/webinar.model';
import { RBEvent } from './event.model';
import { MainService } from 'rainbow-web-sdk/services';
import { CONVERSATION_SVC, ConversationServiceRB, SendFileMessageData, SendMessageData } from 'rainbow-web-sdk/services/conversation/conversation.service';
import { User } from './user.model';
import { SearchTextResult } from './searchTextConvResults.model';
import { FileDocument } from './fileDocument.model';
import { EmitableService, EventableService, makeServiceEvent, RbServiceEvent } from '../services/service';
import { MaybeArray } from '../typings/helpers';

/**
 * Conversation public events
 * @eventProperty
 */
export enum ConversationEvents {

    /**
    * @eventProperty
    * This RBEvent is send when a new message is added in this conversation
    */
    ON_MESSAGE_ADDED = 'ON_MESSAGE_ADDED',

    /**
     * @eventProperty
     * This RBEvent is send when the replaced/deleted message is the last one
     */
    ON_MESSAGE_REPLACED = 'ON_MESSAGE_REPLACED',

    /**
     * @eventProperty
     * This RBEvent is send when a bookmark is set in this conversation
     */
    ON_MESSAGE_BOOKMARKED = 'ON_MESSAGE_BOOKMARKED',

    /**
    * @eventProperty
    * This RBEvent is send when a message is add in the "cache message".
    * This "cache message" is used to store messages which belongs to the conversation but not
    * already loaded. For example an old replied message.
    */
    ON_MESSAGE_CACHED = 'ON_MESSAGE_CACHED',

    /**
     * @eventProperty
     * This RBEvent is send when something occurs on conversation messages
     */
    ON_MESSAGES_UPDATED = 'ON_MESSAGES_UPDATED',

    /**
     * @eventProperty
     * This RBEvent is send when conversation messages are cleared
     */
    ON_MESSAGES_CLEARED = 'ON_MESSAGES_CLEARED',

    /**
     * @eventProperty
     * This RBEvent is send when the missed message counter is updated
     */
    ON_MISSED_COUNTER_UPDATED = 'ON_MISSED_COUNTER_UPDATED',

    /**
    * @eventProperty
    * This RBEvent is send when the conversation status is changed
    * This status represent the user typing activity.
    * + ACTIVE
    * + INACTIVE
    * + COMPOSING
    * + PAUSED
    */
    ON_STATUS_CHANGED = 'ON_STATUS_CHANGED',

    /**
     * @eventProperty
     * This RBEvent is send when
     * In some situations when the call is "remotely picked" by another client and we update the tel call area (??)
     * In some situations to manage the mute / unmute / sharing buttons in tell call area;
     * This event is to be DELETED and replaced with something else;
     */
    RAINBOW_CONVERSATION_CALL_UPDATE = 'RAINBOW_CONVERSATION_CALL_UPDATE',

    /**
    * @eventProperty
    * @internal
    * This RBEvent is send when the contact is replced by a new one in the conversation :
    * It is used in the following usecases:
    * - PBX Hybride (OXO/OXE) conference call is "reverted" to single call
    * - temp contact replaced by a directory contact
    * As the conversation stays the same, we only modify the "contact" inside to adapt to the new situation.
    */
    ON_CONVERSATION_CALL_CONTACT_CHANGE = 'ON_CONVERSATION_CALL_CONTACT_CHANGE',

    // ************************************************** //
    // ** EVENTS TO REWORK : UCAAS SPECIFIC EVENTS     ** //
    // ************************************************** //

    /**
    * @eventProperty
    * @internal
    * This RBEvent is send when
    * FIXME : MCO GUI EVENT
    */
    //NOTE DOBRO -> make this capapbility of the conversation ??
    ON_CONVERSATION_CALL_SHIFT_CHANGE = 'ON_CONVERSATION_CALL_SHIFT_CHANGE',

    /**
    * @eventProperty
    * @internal
    * This RBEvent is send when
    * FIXME : MCO GUI EVENT
    */
    RAINBOW_CONVERSATION_USER_UPDATED = 'RAINBOW_CONVERSATION_USER_UPDATED',

    /**
    * @eventProperty
    * @internal
    * This RBEvent is send when
    * FIXME : MCO GUI EVENT
    */
    RAINBOW_CONVERSATION_ROOM_UPDATED = 'RAINBOW_CONVERSATION_ROOM_UPDATED',

    /**
     * @eventProperty
     * @internal
     * This RBEvent is send when
     * FIXME : MCO WRONG PLACE : SFUCALLAREA SERVICE EVENT : GUI
     */
    RAINBOW_CONVERSATION_REQUEST_TRANSFERT_SHARING = 'RAINBOW_CONVERSATION_REQUEST_TRANSFERT_SHARING',

    /**
     * @eventProperty
     * @internal
     * This RBEvent is send when
     * FIXME : MCO WRONG PLACE : SFUCALLAREA SERVICE EVENT : GUI
     */
    RAINBOW_CONVERSATION_ACCEPT_TRANSFERT_SHARING = 'RAINBOW_CONVERSATION_ACCEPT_TRANSFERT_SHARING',

    /**
     * @eventProperty
     * @internal
     * This RBEvent is send when the conversation urgency level is reset
     * FIXME : MCO GUI EVENT
     */
    RAINBOW_CONVERSATION_RESET_MESSAGE_LEVEL = 'RAINBOW_CONVERSATION_RESET_MESSAGE_LEVEL',

    /**
     * @eventProperty
     * @internal
     * This RBEvent is send when
     * FIXME : MCO GUI EVENT
     */
    RAINBOW_CONVERSATION_INFO_VISIBLE = 'RAINBOW_CONVERSATION_INFO_VISIBLE'
}

/**
 * Conversation public events
 */
export interface IConversationEvents {

    /**
    * @eventProperty
    * This RBEvent is send when a new message is added in this conversation
    */
    ON_MESSAGE_ADDED: {
        message: Message;
    };

    /**
     * @eventProperty
     * This RBEvent is send when the replaced/deleted message is the last one
     */
    ON_MESSAGE_REPLACED: {
        message: Message;
    };

    /**
     * @eventProperty
     * This RBEvent is send when a bookmark is set in this conversation
     */
    ON_MESSAGE_BOOKMARKED: {
        bookmark: ConversationBookmark;
    };

    /**
    * @eventProperty
    * This RBEvent is send when a message is add in the "cache message".
    * This "cache message" is used to store messages which belongs to the conversation but not
    * already loaded. For example an old replied message.
    */
    ON_MESSAGE_CACHED: {
        message: Message;
    };

    /**
     * @eventProperty
     * This RBEvent is send when something occurs on conversation messages
     */
    ON_MESSAGES_UPDATED: unknown;

    /**
     * @eventProperty
     * This RBEvent is send when conversation messages are cleared
     */
    ON_MESSAGES_CLEARED: unknown;

    /**
     * @eventProperty
     * This RBEvent is send when the missed message counter is updated
     */
    ON_MISSED_COUNTER_UPDATED: { missedCounter: number; };

    /**
    * @eventProperty
    * This RBEvent is send when the conversation status is changed
    * This status represent the user typing activity.
    * + ACTIVE
    * + INACTIVE
    * + COMPOSING
    * + PAUSED
    */
    ON_STATUS_CHANGED: {
        status: ConversationStatus;
        composers: User[];
    };

    /**
     * @eventProperty
     * This RBEvent is send when
     * In some situations when the call is "remotely picked" by another client and we update the tel call area (??)
     * In some situations to manage the mute / unmute / sharing buttons in tell call area;
     * This event is to be DELETED and replaced with something else;
     */
    RAINBOW_CONVERSATION_CALL_UPDATE: { call: Call; };

    /**
    * @eventProperty
    * @internal
    * This RBEvent is send when the contact is replced by a new one in the conversation :
    * It is used in the following usecases:
    * - PBX Hybride (OXO/OXE) conference call is "reverted" to single call
    * - temp contact replaced by a directory contact
    * As the conversation stays the same, we only modify the "contact" inside to adapt to the new situation.
    */
    ON_CONVERSATION_CALL_CONTACT_CHANGE: { call: Call; };

    // ************************************************** //
    // ** EVENTS TO REWORK : UCAAS SPECIFIC EVENTS     ** //
    // ************************************************** //

    /**
    * @eventProperty
    * @internal
    * This RBEvent is send when
    * FIXME : MCO GUI EVENT
    */
    //NOTE DOBRO -> make this capapbility of the conversation ??
    ON_CONVERSATION_CALL_SHIFT_CHANGE: unknown;

    /**
    * @eventProperty
    * @internal
    * This RBEvent is send when
    * FIXME : MCO GUI EVENT
    */
    RAINBOW_CONVERSATION_USER_UPDATED: unknown;

    /**
    * @eventProperty
    * @internal
    * This RBEvent is send when
    * FIXME : MCO GUI EVENT
    */
    RAINBOW_CONVERSATION_ROOM_UPDATED: unknown;

    /**
     * @eventProperty
     * @internal
     * This RBEvent is send when
     * FIXME : MCO GUI EVENT
     */
    RAINBOW_CONVERSATION_INFO_VISIBLE: unknown;

    /**
     * @eventProperty
     * @internal
     * This RBEvent is send when the conversation urgency level is reset
     * FIXME : MCO GUI EVENT
     */
    RAINBOW_CONVERSATION_RESET_MESSAGE_LEVEL: unknown;

    /**
     * @eventProperty
     * @internal
     * This RBEvent is send when
     * FIXME : MCO WRONG PLACE : SFUCALLAREA SERVICE EVENT : GUI
     */
    RAINBOW_CONVERSATION_REQUEST_TRANSFERT_SHARING: {
        fromContact: any;
        from: any;
    };

    /**
     * @eventProperty
     * @internal
     * This RBEvent is send when
     * FIXME : MCO WRONG PLACE : SFUCALLAREA SERVICE EVENT : GUI
     */
    RAINBOW_CONVERSATION_ACCEPT_TRANSFERT_SHARING: {
        fromContact: any;
        from: any;
    };
}

/**
 * This enum is used to represent the type of the conversation
 */
export enum ConversationType {
    P2P = 'P2P',
    BUBBLE = 'BUBBLE',
    BOT = 'BOT'
}

/**
 * This enum is used to represent the status of the conversation
 */
export enum ConversationStatus {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    COMPOSING = 'composing',
    PAUSED = 'paused'
}

export interface DocumentMessageData {
    /**
     * The document name to be shown in the conversation
     */
    documentName?: string;

    /**
     * The Document to send ( in case of already existing document)
     */
    document?: FileDocument;

    /**
     * The urgency of the message; Allowed values are (standard) 'std', (notify) 'low', (warning) 'middle', (emergency) 'high'; Default value is "std"
     */
    urgency?: Urgency;
}
export class ConversationLastMessage {
    /**
     * @internal
     */
    constructor() { }

    /**
     * The text of the last message received of the conversation
     * @readonly
     */
    text: string = '';

    /**
     * The content type of the last message received of the conversation
     * @readonly
     */
    contentType: string = '';

    /**
     * The ID of the user for the last message
     * @readonly
     */
    sender: string | null = null;

    /**
     * The Side of the Last message.
     * + LEFT
     * + RIGHT
     * + ADMIN
     * @readonly
     */
    side?: MessageSide;

    /**
     * The ID the last message replaced by
     * @readonly
     */
    messageId: string = '';

    /**
     * Allow to reset the lastMessage parameters
     */
    public reset() {
        this.text = '';
        this.contentType = '';
        this.sender = '';
        this.messageId = '';
        this.side = undefined;
    }
}

/**
 * This class is used to represent a conversation capabilities
 */
export class ConversationCapabilities {
    /**
     * @internal
     */
    constructor() { }

    /**
     * User can make or receive telephony calls
     */
    public telephony: boolean = false;

    /**
     * User can make / receive webrtc calls
     */
    public webRTC: boolean = false;

    /**
     * User can make a desktop sharing call with that user
     */
    public sharedDesktop: boolean = false;

    /**
     * Means telephony || webRTC is TRUE
     */
    public mediaAvailable: boolean = false;

    /**
     * True if user is already in call and has an active fullJid
     */
    public addMedia: boolean = false;
}

/**
 * This class is used to represent a conversation missed info counters
 */
export class ConversationMissedInfoCounters {
    /**
     * @internal
     */
    constructor() { }

    /**
    * @internal
    */
    public static create() { return new ConversationMissedInfoCounters(); }

    public missedIMCounter: number = 0;
    public missedIMDetails: any[] = [];

}

export class ConversationBookmark {
    /**
    * @internal
    */
    constructor() { }

    public messageId: MessageID;
    public timestamp: number = -1;
}

export type MessageLevel = 'standard' | 'notify' | 'warning' | 'emergency';

/**
 * A conversation modelize an interaction between one or more Rainbow users.
 * In the Rainbow ecosystem we'll come across two types of conversations:
 * + The "peer-to-peer" (P2P) conversations : where two Rainbow users interact with each other.
 * + The "bubble" conversations : where several users interact all together.
 */
export interface Conversation extends EventableService<IConversationEvents> {

    /**
     * The conversation ID
     * @readonly
     */
    id: string;

    /**
     * The database ID
     * @readonly
     */
    dbId?: string;

    /**
     * The conversation name
     * @readonly
     */
    name?: string;

    /**
     * The type of the conversation. Can be P2P (0), BUBBLE (1) or BOT (2)
     * @readonly
     */
    type?: ConversationType;

    /**
     * (P2P conversation only) The recipient of the conversation
     * @readonly
     */
    user?: User;

    /**
     * (Bubble conversation only) The conversation Bubble if any
     * @readonly
     */
    bubble?: Bubble;

    /**
     * The capabilities of the conversation
     * @readonly
     */
    capabilities?: ConversationCapabilities;

    /**
     * The name of the conversation (the display name of the recipient for P2P conversation or the room name for a BUBBLE conversation)
     * @readonly
     */
    filterName: string;

    /**
     * The number of instant messages not read
     * @readonly
     */
    missedCounter: number;

    /**
     * The list of messages downloaded for this conversation
     * @readonly
     */
    messages: Message[];

    /**
     * The status of the conversation
     * @readonly
     */
    status: ConversationStatus;

    /**
     * The date of the last modification of the conversation
     * @readonly
     */
    lastModification?: Date;

    /**
     * The date of the creation of the conversation
     * @readonly
     */
    creationDate?: Date;

    /**
     * The last message received of the conversation
     * @readonly
     */
    lastMessage: ConversationLastMessage;

    /**
     * If the last message that is forwarded is a file
     * @readonly
     */
    isForwardedMessageFile: boolean; // TODO : MCO ask Dobro WTF ?

    /**
     * The last message forwarded
     * @readonly
     */
    forwardedMessage: string;

    /**
     * TRUE if conversation favorite
     * @readonly
     */
    isFavorite: boolean;

    /**
     * The bookmarked message in the conversation
     */
    bookmark: ConversationBookmark;

    /**
     * True if this conversation is a webinar conversation
     * @readonly
     */
    isWebinarConversation: boolean;

    /**
     * The webinar associated to this conversation (if any)
     * @readonly
     */
    webinar?: Webinar;

    call: CallRB;

    /**
    * Subscribe to updates on the current conversation (all events are of RBEvent type);
    * @param handler - The call-back function that will be subscribed to the RxJS subject
    * @param filterEvent - a list of event names that the user wants to receive when subscribing (used a filter)
    */
    subscribe(handler: (event: RBEvent<keyof IConversationEvents>) => any, filterEvent?: MaybeArray<keyof IConversationEvents>): Subscription;

    /**
     * This method allows us to retrieve archived message pages.
     * By default, no message history is loaded, but this method will allow us to retrieve them page by page.
     * @param nbMessages - the number of messages to get in this page
     */
    getHistoryPage(nbMessages: number): Promise<void>;

    /**
     * Return all the already loaded messages
     */
    getMessages(): Message[];

    /**
    * Remove given message from the conversation (should be a message that the user has sent)
    * @param message - the message we want to delete
    */
    deleteMessage(message: Message): void;

    /**
    * Remove all message of the conversation
    */
    deleteAllMessages(): Promise<void>;

    /**
     * Return the message with the corresponding identifier
     * @param messId - the message identifier
     * @param searchInCache - if true this methods start to search in cacheMessage
     * @returns the search message if founded undefined else
     */
    getMessageById(messId: MessageID, searchInCache?: boolean): Message | undefined;

    /**
     * This method permits to send a chat Message
     * @param messageText - the text to be send
     * @param messageData - the message data object
     * @returns the sent message (or undefined in case of an issue)
     */
    sendMessage(messageText: string, messageData?: SendMessageData): Message | undefined;

    /**
     * This method permits to send a file Message
     * @param messageData - The document message data to be send
     */
    sendFileMessage(messageData: SendFileMessageData): Promise<Message>;

    /**
     * This method permits to send a file document message
     * @param messageData - The file message data to be send
     */
    sendFileDocumentMessage(messageData: DocumentMessageData): Promise<Message>;

    /**
     * Method to set or replace conversation bookmark
     * This API will provoque an update on the conversation object (event of type ConversationEvents.ON_MESSAGE_BOOKMARKED)
     * @param message - the message to bookmark
     */
    setBookmark(message: Message): Promise<void>;

    /**
     * Method to delete a conversation bookmark
     */
    deleteBookmark(): Promise<void>;

    /**
     * This API can be used to add an emoji reaction to an already existing message.
     * @param message - The Message we want to react to
     * @param emojiReaction - The emoji reaction to add
     * @returns the updated message or undefined in case of issue
     */
    addReactionToMessage(message: Message, emojiReaction?: string): Promise<Message>;

    /**
     * The API can be used to remove an emoji reaction we previously added from a message
     * @param message - The message from which we want to remove the reaction
     * @param emojiReaction - The emoji reaction to remove
     * @returns the updated message or undefined in case of issue
     */
    removeReactionFromMessage(message: Message, emojiReaction?: string): Promise<Message>;

    /**
     * This API can be used to modify an already existing sent message; The user is allowed to modify the text of the message, the additional content and the mentions;
     * NB The new SendMessageData will replace the existing data, so all the existing informations (additionalContent or mentions) should also be added in the data otherwise they'll be removed
     * @param message - The Message we want to modify
     * @param newMessageText - The new text of the message
     * @param messageData - the updated send message data
     * @returns the updated message or undefined in case of issue
     */
    modifyMessage(message: Message, newMessageText?: string, messageData?: SendMessageData): Message;

    /**
     * This method permits to forward a Message to another conversation
     * @param destConv - conversation to which the Message is forwarded
     * @param message - the message to forward
     * @param anonymous - allow to specify if yes or not the message orioginbator has to be displayed (default: true)
     */
    forwardMessage(destConv: Conversation, message: Message, anonymous?: boolean): Promise<Message | undefined>;

    /**
     * This method permits to send a "typing" (or remove it) state to the distant party;
     * @param isTypingState - if the user is currently "typing" inside the conversation
     */
    sendIsTypingState(isTypingState: boolean): void;

    /**
     * This API permit to send the text content of the conversation at the connectUser e-mail address.
     */
    sendByEmail(): Promise<void>;

    /**
     * This method permits to send a ACK READ status for message to mark it as READ
     * @param message - the chat message to "ACK read"
     */
    ackReadMessage(message: Message): void;

    /**
     * This method permits to send a ACK READ all received messages inside the conversation
     */
    ackReadMessages(): boolean;

    /**
     * Method used to search for text in a given conversation P2P or bulle. The searched text length should be 3+ characters.
     * This API will return a SearchTextResult that contains the list of IDs of messages to be searched after that
     * @param searchedText - the text to be searched
     * @param limit - number of results to get; Default value is 20;
     */
    searchForText(searchedText: string, limit: number): Promise<SearchTextResult>;

    /**
     *  Method used to load the messages around a given timestamp. Can be used when we search for a message via API searchForTextInConversation
     * and then we want to load the message (or also the messages around it); This API will load the messages in the conversation list of messages and resolve once it's done;
     * @param timestamp - the timestamp of the message to be loaded
     * @param nbMessages - the number of messages to load before and after this one
     */
    loadHistoryMessagesAroundGivenMessage(timestamp: number, nbMessages: number): Promise<void>;

    /**
     * JSON serialisation of conversation for debug purpose
     */
    toJSON(): any;
}

/**
 * @internal
 * This class is used to represent a conversation
 */
export class ConversationRB implements Conversation, EmitableService<IConversationEvents> {

    private static randomBase = crypto.randomUUID();
    private static messageId = 0;

    public static ChatstatesNS: string = 'http://jabber.org/protocol/chatstates';
    public static ReceiptNS: string = 'urn:xmpp:receipts';
    public static contentNS: string = 'urn:xmpp:content';
    public static AttentionNS: string = 'urn:xmpp:attention:0';
    public static FORWARDNS: string = 'urn:xmpp:forward:0';

    // ************************************************************ //
    // ** STATIC FACTORIES                                       ** //
    // ************************************************************ //
    public static createConversation(peer: Contact | BubbleRB): ConversationRB {

        // Peer is a Contact
        if (peer instanceof Contact) {
            const contact: Contact = peer;
            const conversation = new ConversationRB(contact.id);
            conversation.contact = contact;
            conversation.type = contact.isBot ? ConversationType.BOT : ConversationType.P2P;
            conversation.infoVisible = contact.isBot;
            conversation.filterName = contact.displayName ? contact.displayName.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '') : null;
            contact.conversation = conversation;
            return conversation;
        }

        // Peer is a bubble
        const bubble: BubbleRB = peer;
        const conversation = new ConversationRB(bubble.jid);
        conversation.type = ConversationType.BUBBLE;
        conversation.bubble = bubble;
        conversation.filterName = bubble.name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        conversation.infoVisible = true;
        return conversation;
    }

    public static getUniqueMessageId(): MessageID {
        const messageToSendID = 'web_' + ConversationRB.randomBase + (ConversationRB.messageId++);
        return messageToSendID as MessageID;
    }

    public id: string;
    public dbId?: string;
    public type?: ConversationType;
    public filterName: string = '';
    public contact: Contact | null = null;
    public bubble?: BubbleRB;
    public infoVisible: boolean = false;
    public capabilities?: ConversationCapabilities;
    public missedCounter: number = 0;
    public missedCalls: number = 0;
    public messages: Message[] = [];
    public participantStatuses: Record<string, ConversationStatus> = {}; // MCO missing statuses class
    public status: ConversationStatus = ConversationStatus.INACTIVE;
    public historyIndex: number = -1;
    public historyLastIndex: number = -1;
    public historyMessages: Message[] = [];
    public historyDefered = null;
    public historyAboveComplete: boolean = false;
    public historyBelowComplete: boolean = false;
    public currentHistoryIndex?: number;
    public lastModification?: Date;
    public creationDate?: Date;
    public lastMessage: ConversationLastMessage = new ConversationLastMessage();
    public forwardFrom: any = null; // MCO any
    public forwardedMessage: any = null; // MCO any
    public pip: boolean = true;
    public videoCall: CallRB | null = null;
    public audioCall: CallRB | null = null;
    public muted: boolean = false;
    public isMutedAudio: boolean = false;
    public largeInfoVisible: boolean = true;
    public selectedTabName: string = 'chat'; // MCO GUI????
    public isFavorite: boolean = false;
    public cacheMessages: Message[] = [];
    public telephonyContacts: any = null; // MCO any
    public isWebinarConversation: boolean = false;
    public webinar?: Webinar;
    public lastCallInfo: any = null; // MCO ???
    public isForwardedMessageFile: boolean = false;
    public isGeoLoc?: boolean = false;
    private _isDraft: boolean = false;
    public replyData: AnswerMessage = null;

    public lastEvent: any = null;

    private _callShift: boolean = false;
    private _bookmark: ConversationBookmark = null;
    private readonly rxSubject = new Subject<RbServiceEvent<IConversationEvents>>();

    public get getNameForLogs(): string {
        if (this.filterName) {
            return config?.debug ? this.filterName : this.filterName.charAt(0) + this.filterName.replace(/[^\s](?=.{1,}$)/g, "*").substr(1);
        }
        return '';
    }

    public get bookmark(): ConversationBookmark {
        return this._bookmark;
    }

    public set bookmark(value: ConversationBookmark) {
        if (this._bookmark !== value) {
            this._bookmark = value;
            this.rxSubject.next(RBEvent.create(ConversationEvents.ON_MESSAGE_BOOKMARKED, { bookmark: value }));
        }
    }

    private _shouldRemoveBookMark: boolean = false;
    public get shouldRemoveBookMark(): boolean {
        return this._shouldRemoveBookMark;
    }

    public set shouldRemoveBookMark(value: boolean) {
        this._shouldRemoveBookMark = value;
        this.rxSubject.next(RBEvent.create(ConversationEvents.ON_MESSAGE_BOOKMARKED));
    }

    // Workaround to keep working conversation.room, but actually there's the conversation.bubble behind
    public get room(): BubbleRB { return this.bubble; }
    public set room(value: BubbleRB) { this.bubble = value; }

    public get isDraft(): boolean { return this._isDraft; }

    public set isDraft(value: boolean) {
        this._isDraft = value;
        this.rxSubject.next(RBEvent.create(ConversationEvents.ON_CONVERSATION_CALL_SHIFT_CHANGE));
    }

    public set callShift(callShift: boolean) { if (this._callShift !== callShift) this._callShift = callShift; this.rxSubject.next(RBEvent.create(ConversationEvents.ON_CONVERSATION_CALL_SHIFT_CHANGE)); }
    public get callShift() { return this._callShift; }

    public get call(): CallRB {
        if (this.audioCall) return this.audioCall;
        if (this.videoCall) return this.videoCall;
        return null;
    }

    public get name(): string { return this.type === ConversationType.P2P ? this.user.displayName : this.bubble?.name; }
    public get user(): User { return this.contact; }

    private conversationService?: ConversationServiceRB;

    // ********************************************************************** //
    // ** PUBLIC METHODS IMPLEMENTATION                                    ** //
    // ********************************************************************** //

    public getHistoryPage(nbMessages: number = 30): Promise<void> {
        return this.conversationService.conversationServiceBulkHistoryHandler.getHistoryPage(this, nbMessages);
    }

    public getMessages(): Message[] {
        return this.messages;
    }

    public sendMessage(messageText: string, messageData?: SendMessageData): Message | undefined {
        return this.conversationService?.sendMessage(this, messageText, messageData);
    }

    public sendFileDocumentMessage(messageData: DocumentMessageData): Promise<Message> {
        const sendFileMessageData: SendFileMessageData = { fileName: messageData.documentName, document: messageData.document, urgency: messageData.urgency };
        return this.conversationService?.sendFileMessage(this, sendFileMessageData);
    }

    public sendFileMessage(messageData: SendFileMessageData): Promise<Message> {
        return this.conversationService?.sendFileMessage(this, messageData);
    }

    public deleteMessage(message: Message): void {
        this.conversationService?.deleteMessage(this, message);
    }

    public deleteAllMessages(): Promise<void> {
        return this.conversationService?.removeAllMessages(this);
    }

    public setBookmark(message: Message): Promise<void> {
        return this.conversationService?.setConversationBookmark(this, message);
    }

    public deleteBookmark(): Promise<void> {
        return this.conversationService?.deleteConversationBookmark(this);
    }

    public async addReactionToMessage(message: Message, emojiReaction?: string): Promise<Message> {
        return this.conversationService?.addReactionToMessage(this, message, emojiReaction);
    }

    public async removeReactionFromMessage(message: Message, emojiReaction?: string): Promise<Message> {
        return this.conversationService?.removeReactionFromMessage(this, message, emojiReaction);
    }

    public modifyMessage(message: Message, newMessageText?: string, messageData?: SendMessageData): Message {
        return this.conversationService?.modifyMessage(this, message, newMessageText, messageData);
    }

    public forwardMessage(destConv: Conversation, message: Message, anonymous: boolean = true): Promise<Message | undefined> {
        return this.conversationService?.forwardMessage(this, destConv as ConversationRB, message, anonymous);
    }

    public sendIsTypingState(isTypingState: boolean): void {
        this.conversationService?.sendIsTypingState(this, isTypingState);
    }

    public sendByEmail(): Promise<void> {
        return this.conversationService?.sendConversationByEmail(this);
    }

    public ackReadMessage(message: Message): void {
        this.conversationService?.sendAckReadMessage(this, message);
    }

    public ackReadMessages(): boolean {
        return this.conversationService?.sendAckReadMessages(this);
    }

    public searchForText(searchedText: string, limit: number = 20): Promise<SearchTextResult> {
        return this.conversationService?.searchForTextInConversation(this, searchedText, limit);
    }

    public loadHistoryMessagesAroundGivenMessage(timestamp: number, loadAround: number): Promise<void> {
        return this.conversationService?.loadHistoryMessagesAroundGivenMessage(this, timestamp, loadAround);
    }

    public constructor(conversationId: string) {
        this.conversationService = MainService.getInstance().inject<ConversationServiceRB>(CONVERSATION_SVC);
        this.id = conversationId;
        this.creationDate = new Date();
    }

    /**
     * Search in cache the message with the corresponding identifier
     * @param messId - the message identifier
     */
    private getMessageInCacheById(messId: string): Message | undefined {
        return this.cacheMessages.find((item: Message) => { return item.id === messId; });
    }

    public getMessageById(messId: MessageID, searchInCache: boolean = false): Message {
        if (searchInCache) { const foundMsg = this.getMessageInCacheById(messId); if (foundMsg) return foundMsg; }
        return this.messages.find(item => item.id === messId);
    }

    /**
     * Search in history the message with the corresponding identifier
     * @param messId - the message identifier
     */
    public getMessageByIdInHistory(messId: MessageID): Message {
        return this.historyMessages.find((item) => { return item.id === messId; });
    }

    /**
     * get a replaced message by identifier
     * @param messId - the message identifier
     */
    public getReplacedMessageById(messId: MessageID): Message | null {
        let message = null;
        this.messages.forEach((msg) => { message = msg.replaceMsgs.find((replaceMsg) => { return replaceMsg.id === messId; }); });
        return message;
    }

    /**
     * Reset conversation instance
     */
    public reset(): void {
        this.messages = [];
        this.historyIndex = -1;
        this.historyLastIndex = -1;
        this.historyMessages = [];
        this.historyAboveComplete = false;
        this.historyBelowComplete = false;
        this.currentHistoryIndex = undefined;
        this.lastMessage.reset();
        this.sendEvent(ConversationEvents.ON_MESSAGES_UPDATED, undefined); // kept after adding ON_MESSAGES_CLEARED just in case
        this.sendEvent(ConversationEvents.ON_MESSAGES_CLEARED, undefined);
    }

    public resetHistoryInfo(): void {
        this.historyIndex = -1;
        this.historyLastIndex = -1;
        this.historyMessages = [];
        this.historyAboveComplete = false;
        this.historyBelowComplete = false;
        this.currentHistoryIndex = undefined;
    }

    public subscribe(handler: (event: RBEvent<keyof IConversationEvents>) => any, eventNames?: MaybeArray<keyof IConversationEvents>): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event) => {
                if (Array.isArray(eventNames)) return eventNames.includes(event.name);
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event) => {
            try { handler(event); }
            catch (error) { /*this.logger.error(`[Conversation] subscribe () -- error -- ${error.message}`);*/ }
        });
    }

    public events<const EventNames extends never>(...eventNames: EventNames[]) {
        return makeServiceEvent(this.rxSubject, ...eventNames);
    }

    public sendEvent<const EventName extends keyof IConversationEvents>(eventName: EventName, data: IConversationEvents[EventName]): void {
        this.rxSubject.next(RBEvent.create(eventName, data));
    }

    /**
     * Subscribe to conversation change observable
     * @param missedCounter - the missed message value to set
     */
    public setMissedCounter(missedCounter: number): void {
        if (this.missedCounter !== missedCounter) {
            this.missedCounter = missedCounter;
            this.rxSubject.next(RBEvent.create(ConversationEvents.ON_MISSED_COUNTER_UPDATED, { missedCounter }));
        }
    }

    /**
     * Check if conversation has an incomming call
     */
    public isAudioCallIncomming(): boolean {
        return (this.audioCall?.callStatus
            && (this.audioCall.callStatus === CallStatus.QUEUED_INCOMMING || this.audioCall.callStatus === CallStatus.RINGING_INCOMMING));
    }

    /**
     * Add message in history messages
     * @param message - the message
     */
    public addMessageInHistoryMessages(message: Message): void {
        if (!message) { return; }
        this.historyMessages.push(message);
    }

    /**
     * Add message in the cache (these are messages that are part of the conversation but often not in the right place, like in case
     * of some old reply or something like that, so the message is added in the cache and not in the list of messages, as it's separated)
     * @param message - the message
     */
    public addMessageInCacheMessages(message: Message): void {
        if (!message) { return; }
        const cacheMsg = this.getMessageInCacheById(message.id);
        if (cacheMsg) { return; }
        this.cacheMessages.push(message);
        this.sendEvent(ConversationEvents.ON_MESSAGE_CACHED, { message });
    }

    public ackReadAllSentMessages = function () {
        this.messages.forEach((message) => {
            if (message.side === MessageSide.RIGHT && (message.receiptStatus === MessageStatus.SENT || message.receiptStatus === MessageStatus.UNREAD)) {
                message.setReceiptStatus(MessageStatus.READ);
            }
        });
    };

    public ackReceivedAllSentMessages(): void {
        this.messages.forEach((message) => {
            if (message.side === MessageSide.RIGHT && message.receiptStatus === MessageStatus.SENT) {
                message.setReceiptStatus(MessageStatus.UNREAD);
            }
        });
    }

    /**
     * Marks all received messages as READ
     */
    public ackReadAllReceivedMessages(): boolean {
        let result = false;
        this.messages.forEach((message: Message) => {
            if (message.side !== MessageSide.RIGHT && (message.receiptStatus === MessageStatus.SENT || message.receiptStatus === MessageStatus.UNREAD)) {
                message.setReceiptStatus(MessageStatus.READ);
                result = true;
            }
        });

        return result;
    }

    public updateAfterReplaceEvent(message: Message): void {
        // Update conversation info if the replaced/deleted message is the last one
        const lastMsg = this.messages[this.messages.length - 1];
        if (lastMsg?.id === message.id) {
            this.lastModification = new Date();
            this.lastMessage.text = message.getLastTextModified();
            this.lastMessage.sender = message.from.id;
            this.lastMessage.messageId = lastMsg.id;
        }
        this.sendEvent(ConversationEvents.ON_MESSAGE_REPLACED, { message });
        message.sendUpdateEvent();
    }

    public toJSON(): any {
        const json: any = {
            id: this.dbId,
            jid: this.id,
            type: this.type,
            name: this.name
        };
        if (this.bookmark) json.bookmark = this.bookmark;

        if (this.call) {
            json.call = {};
            json.call.id = this.call?.id;
            json.call.callType = this.call?.callType;
            json.call.status = this.call?.status;
        }
        return json;
    }
}
